import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { launchpoolItemBunja, useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { useUserTier } from '@flipster/features/src/shared/user-tier/state';
import { useOverlay } from '@flipster/overlay';
import { alert } from '@flipster/utils/alert';
import segment, { WalletEvent } from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import StakingModal from '../../_modals/staking-modal';
import { getVIPEstimatedApr } from '../utils';

export default function useStakeFlow() {
  const router = useRouter();
  const overlay = useOverlay();

  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { isLoggedIn, isValidKyc } = useAuth();
  const { isAchievedLevel, kycInfo } = useKYC();
  const { userTier } = useUserTier();
  const { launchpoolList } = useLaunchpool();

  return function startStake() {
    segment.track(StakeEvent.StakeButtonClick, {
      symbol: launchpool.stakingCurrency,
      apr: launchpool.estimatedApr,
      reward_amount: launchpool.estimatedReward,
      total_quantity: launchpool.totalStaked,
      my_quantity: launchpool.stakedAmount,
      apr_vip: getVIPEstimatedApr(launchpool.estimatedApr, launchpool.vipMultiplier),
      vip_status: userTier?.status,
    });

    if (!isLoggedIn) {
      router.push('/signin');
      return;
    }

    if (!isValidKyc) {
      alert.show({
        title: 'Verification required',
        message: 'Please verify your identity to withdraw currencies.',
        confirmText: 'Verify',
        withClose: true,
        onConfirm() {
          segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
          router.push('/user/kyc');
        },
        onClose() {
          segment.track(WalletEvent.VerifyCancelButton);
        },
      });
      return;
    }

    if (!isAchievedLevel) {
      alert.show({
        title: 'Identity verification',
        message: 'Required to participate in this event.',
        confirmText: 'Verify now',
        closeText: 'Cancel',
        withClose: true,
        onConfirm() {
          segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
          router.push('/user/kyc');
        },
        onClose() {
          segment.track(WalletEvent.VerifyCancelButton);
        },
      });
      return;
    }

    if (launchpoolList?.isAbuserOrInternal) {
      alert.show({
        title: 'Staking unavailable',
        message: 'Due to policy restrictions, staking is unavailable.',
        confirmText: 'Close',
      });
      return;
    }

    overlay.open((close) => <StakingModal launchpoolId={launchpool.id} onClose={close} />);
  };
}
